
import * as React from "react"
import { cn } from "@/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"

const cardVariants = cva(
  "rounded-lg border bg-card text-card-foreground shadow-md hover:shadow-lg transition-all duration-200",
  {
    variants: {
      density: {
        default: "",
        compact: "p-2 sm:p-3 md:p-4 lg:p-6",
        normal: "p-3 sm:p-4 md:p-5 lg:p-6",
        spacious: "p-4 sm:p-6 md:p-7 lg:p-8",
      },
    },
    defaultVariants: {
      density: "default",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, density, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ density }), className)}
      {...props}
    />
  )
)
Card.displayName = "Card"

const cardHeaderVariants = cva(
  "flex flex-col space-y-1.5",
  {
    variants: {
      density: {
        default: "p-3 sm:p-6",
        compact: "p-2 sm:p-3 md:p-4 lg:p-6",
        normal: "p-3 sm:p-4 md:p-5 lg:p-6", 
        spacious: "p-4 sm:p-6 md:p-7 lg:p-8",
      },
    },
    defaultVariants: {
      density: "default",
    },
  }
)

export interface CardHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardHeaderVariants> {}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, density, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardHeaderVariants({ density }), className)}
      {...props}
    />
  )
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-lg sm:text-xl md:text-2xl font-bold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-xs sm:text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const cardContentVariants = cva(
  "",
  {
    variants: {
      density: {
        default: "p-3 pt-0 sm:p-6",
        compact: "p-2 pt-0 sm:p-3 md:p-4 lg:p-6",
        normal: "p-3 pt-0 sm:p-4 md:p-5 lg:p-6",
        spacious: "p-4 pt-0 sm:p-6 md:p-7 lg:p-8",
      },
    },
    defaultVariants: {
      density: "default",
    },
  }
)

export interface CardContentProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardContentVariants> {}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, density, ...props }, ref) => (
    <div 
      ref={ref} 
      className={cn(cardContentVariants({ density }), className)} 
      {...props} 
    />
  )
)
CardContent.displayName = "CardContent"

const cardFooterVariants = cva(
  "flex items-center",
  {
    variants: {
      density: {
        default: "p-3 pt-0 sm:p-6",
        compact: "p-2 pt-0 sm:p-3 md:p-4 lg:p-6",
        normal: "p-3 pt-0 sm:p-4 md:p-5 lg:p-6",
        spacious: "p-4 pt-0 sm:p-6 md:p-7 lg:p-8",
      },
    },
    defaultVariants: {
      density: "default",
    },
  }
)

export interface CardFooterProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardFooterVariants> {}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, density, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardFooterVariants({ density }), className)}
      {...props}
    />
  )
)
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
